import React, {
  useEffect,
  useRef,
  useState,
} from "react";

import "./RadiationNuclearPage.css";
import { getApiBase } from "../api/healthThreatsApi";

const API_BASE = getApiBase();

// Internal key used by the existing RN model filenames and API.
// The incident location is determined exclusively by center_lat/center_lon.
const RN_MODEL_KEY = "FRANCE";
const THREAT = "RN";

const ACTIVE_JOB_KEY =
  "rn_active_training_job";

const LAST_RESULT_KEY =
  "rn_last_training_result";

const TRAINING_STORAGE_VERSION = 2;

const RN_CDM_COLUMNS = [
  "Data_Origin",
  "Observation_Time",
  "Time_Unit",
  "Wind_U",
  "Wind_V",
  "Precipitation",
  "Distance_X",
  "Distance_Y",
  "Distance_Z",
  "Concentration",
  "Country",
];

const RN_HISTORICAL_DASHBOARD_URL =
  "https://dmht.escort.rid-intrasoft.eu/ui-dashboards/app/dashboards?security_tenant=global#/view/a6187e40-55e9-11f1-bba8-ad1669294be3?embed=true&_g=%28filters%3A%21%28%29%2Cquery%3A%28language%3Akuery%2Cquery%3A%27%27%29%2CrefreshInterval%3A%28pause%3A%21t%2Cvalue%3A0%29%2Ctime%3A%28from%3A%272011-04-07T13%3A57%3A58.187Z%27%2Cto%3A%272011-06-01T14%3A13%3A10.005Z%27%29%29";

const DEFAULT_FORM = {
  scenario: "medium",
  center_lat: "45.8036",
  center_lon: "5.2696",
  start_time: "2025-03-08T08:00",
  simulation_hours: "24",
  wind_u: "5",
  wind_v: "2",
  precipitation: "0",
};

async function apiErrorToMessage(response) {
  const fallback =
    `Request failed (${response.status})`;

  try {
    const contentType =
      response.headers.get("content-type") || "";

    if (contentType.includes("application/json")) {
      const payload = await response
        .json()
        .catch(() => null);

      const detail =
        payload?.detail
        ?? payload?.message
        ?? payload?.error;

      if (typeof detail === "string") {
        return detail;
      }

      if (detail) {
        return JSON.stringify(detail, null, 2);
      }

      return fallback;
    }

    const text = await response
      .text()
      .catch(() => "");

    return text.trim() || fallback;
  } catch {
    return fallback;
  }
}

function loadStored(key) {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveStored(key, value) {
  try {
    sessionStorage.setItem(
      key,
      JSON.stringify(value),
    );
  } catch {
    // Ignore unavailable storage.
  }
}

function removeStored(key) {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Ignore unavailable storage.
  }
}

function numberValue(value, label) {
  const parsed = Number(value);

  if (!Number.isFinite(parsed)) {
    throw new Error(
      `${label} must be a valid number.`,
    );
  }

  return parsed;
}

export default function RadiationNuclearPage() {
  const [modelAvailability, setModelAvailability] =
    useState({ default: false, custom: false });

  const [modelType, setModelType] =
    useState("default");

  const [modelsChecking, setModelsChecking] =
    useState(true);

  const [trainingStatus, setTrainingStatus] =
    useState(null);

  const [trainingPanelOpen, setTrainingPanelOpen] =
    useState(false);

  const [trainingMode, setTrainingMode] =
    useState("existing");

  const [customDatasetFile, setCustomDatasetFile] =
    useState(null);

  const [trainingMessage, setTrainingMessage] =
    useState(null);

  const [trainingProgress, setTrainingProgress] =
    useState(0);

  const [trainingJobId, setTrainingJobId] =
    useState(null);

  const [predictionOpen, setPredictionOpen] =
    useState(false);

  const [predictionStatus, setPredictionStatus] =
    useState(null);

  const [predictionMessage, setPredictionMessage] =
    useState(null);

  const [form, setForm] =
    useState(DEFAULT_FORM);

  const [mapAsset, setMapAsset] =
    useState(null);

  const [mapStatus, setMapStatus] =
    useState(null);

  const [mapMessage, setMapMessage] =
    useState(null);

  const [mapVersion, setMapVersion] =
    useState(0);

  const [historicalOpen, setHistoricalOpen] =
    useState(false);

  const trainingPollRef = useRef(null);
  const predictionAbortRef = useRef(null);

  const modelsReady = (
    modelAvailability.default
    || modelAvailability.custom
  );

  const selectedModelReady = Boolean(
    modelAvailability[modelType]
  );

  const stopTrainingPolling = () => {
    if (!trainingPollRef.current) {
      return;
    }

    clearInterval(trainingPollRef.current);
    trainingPollRef.current = null;
  };

  const checkModels = async () => {
    setModelsChecking(true);

    try {
      const checkModel = async (requestedModelType) => {
        const query = new URLSearchParams({
          country: RN_MODEL_KEY,
          threat: THREAT,
          model_type: requestedModelType,
        });
        const response = await fetch(
          `${API_BASE}/models/exist?${query.toString()}`,
          { cache: "no-store" },
        );
        if (!response.ok) {
          throw new Error(await apiErrorToMessage(response));
        }
        const payload = await response.json();
        return Boolean(payload.ready_for_prediction);
      };

      const [defaultReady, customReady] = await Promise.all([
        checkModel("default"),
        checkModel("custom"),
      ]);
      const availability = {
        default: defaultReady,
        custom: customReady,
      };
      setModelAvailability(availability);
      setModelType((current) => {
        if (availability[current]) return current;
        if (availability.default) return "default";
        if (availability.custom) return "custom";
        return "default";
      });
    } catch {
      setModelAvailability({ default: false, custom: false });
    } finally {
      setModelsChecking(false);
    }
  };

  const finishTraining = (job, jobId) => {
    const activeJob = loadStored(ACTIVE_JOB_KEY);
    const completedMode = (
      job?.metadata?.training_mode
      || activeJob?.trainingMode
      || trainingMode
    );
    const completedModelType = completedMode === "custom"
      ? "custom"
      : "default";
    const result = {
      version: TRAINING_STORAGE_VERSION,
      jobId: job.job_id || jobId,
      country: job.country || RN_MODEL_KEY,
      disease: job.threat || THREAT,
      status: "completed",
      completedAt: new Date().toISOString(),
      message:
        job.message
        || "Radiological-Nuclear model trained successfully.",
      datasetId: completedMode === "custom"
        ? (
          job?.metadata?.filename
          || activeJob?.datasetId
          || "Uploaded RN dataset"
        )
        : "Default RN data",
      datasetSource: "ready",
      trainingSource: completedMode === "custom"
        ? "upload"
        : "idl",
      trainingMode: completedMode,
      modelType: completedModelType,
    };

    saveStored(LAST_RESULT_KEY, result);
    removeStored(ACTIVE_JOB_KEY);

    setTrainingStatus("success");
    setTrainingMessage(result.message);
    setTrainingProgress(100);
    setModelAvailability((current) => ({
      ...current,
      [completedModelType]: true,
    }));
    setModelType(completedModelType);
    setTrainingPanelOpen(true);
    checkModels();
  };

  const startTrainingPolling = (jobId) => {
    if (!jobId) {
      return;
    }

    stopTrainingPolling();

    const poll = async () => {
      try {
        const response = await fetch(
          `${API_BASE}/train/jobs/${jobId}`,
          { cache: "no-store" },
        );

        if (!response.ok) {
          throw new Error(
            await apiErrorToMessage(response),
          );
        }

        const job = await response.json();

        setTrainingJobId(job.job_id || jobId);
        setTrainingProgress(
          Math.max(
            0,
            Math.min(100, Number(job.progress || 0)),
          ),
        );

        if (
          job.status === "queued"
          || job.status === "running"
        ) {
          setTrainingStatus("loading");
          setTrainingMessage(
            job.message
            || "Radiological-Nuclear training is in progress...",
          );
          return;
        }

        stopTrainingPolling();
        removeStored(ACTIVE_JOB_KEY);

        if (job.status === "completed") {
          finishTraining(job, jobId);
          return;
        }

        const message =
          job.status === "cancelled"
            ? job.message || "Radiological-Nuclear training cancelled."
            : job.error
              || job.message
              || "Radiological-Nuclear training failed.";

        saveStored(LAST_RESULT_KEY, {
          version: TRAINING_STORAGE_VERSION,
          jobId: job.job_id || jobId,
          country: RN_MODEL_KEY,
          disease: THREAT,
          status: job.status,
          completedAt: new Date().toISOString(),
          message,
          datasetId: job?.metadata?.filename || "Default RN data",
          datasetSource: "ready",
          trainingSource: job?.metadata?.source || "unknown",
          trainingMode:
            job?.metadata?.training_mode || trainingMode,
        });

        setTrainingStatus("error");
        setTrainingMessage(message);
        setTrainingProgress(0);
      } catch (error) {
        stopTrainingPolling();
        setTrainingStatus("error");
        setTrainingMessage(
          String(error?.message || error),
        );
      }
    };

    poll();
    trainingPollRef.current = setInterval(
      poll,
      2500,
    );
  };

  const startTraining = async () => {
    if (trainingMode === "custom" && !customDatasetFile) {
      setTrainingStatus("error");
      setTrainingMessage(
        "Please select a Radiological-Nuclear CDM CSV before starting training.",
      );
      return;
    }

    setTrainingStatus("loading");
    setTrainingMessage(
      trainingMode === "custom"
        ? "Preparing your dataset..."
        : "Gathering the latest information for training...",
    );
    setTrainingProgress(1);
    removeStored(LAST_RESULT_KEY);

    try {
      const customTraining = trainingMode === "custom";
      const requestOptions = { method: "POST" };

      if (customTraining) {
        const formData = new FormData();
        formData.append("dataset", customDatasetFile);
        requestOptions.body = formData;
      } else {
        requestOptions.headers = {
          "Content-Type": "application/json",
        };
        requestOptions.body = JSON.stringify({
          country: RN_MODEL_KEY,
          threat: THREAT,
          data_format: "json",
          data_source: null,
          json_data: null,
        });
      }

      const response = await fetch(
        `${API_BASE}/train/jobs/rn/${customTraining ? "upload" : "idl"}`,
        requestOptions,
      );

      if (!response.ok) {
        throw new Error(
          await apiErrorToMessage(response),
        );
      }

      const payload = await response.json();

      if (!payload?.job_id) {
        throw new Error(
          "Training job started without job_id.",
        );
      }

      saveStored(ACTIVE_JOB_KEY, {
        version: TRAINING_STORAGE_VERSION,
        jobId: payload.job_id,
        country: RN_MODEL_KEY,
        disease: THREAT,
        trainingMode,
        datasetId: customTraining
          ? customDatasetFile.name
          : "Default RN data",
        datasetSource: "ready",
        trainingSource: customTraining ? "upload" : "idl",
        startedAt: new Date().toISOString(),
      });

      setTrainingJobId(payload.job_id);
      setTrainingMessage(
        customTraining
          ? "Checking your dataset and getting training ready..."
          : "Getting everything ready...",
      );
      startTrainingPolling(payload.job_id);
    } catch (error) {
      removeStored(ACTIVE_JOB_KEY);
      setTrainingStatus("error");
      setTrainingMessage(
        String(error?.message || error),
      );
      setTrainingProgress(0);
    }
  };

  const cancelTraining = async () => {
    const activeJob = loadStored(ACTIVE_JOB_KEY);
    const jobId = trainingJobId || activeJob?.jobId;

    if (!jobId) {
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE}/train/jobs/${jobId}/cancel`,
        { method: "POST" },
      );

      if (!response.ok) {
        throw new Error(
          await apiErrorToMessage(response),
        );
      }

      setTrainingMessage("Cancellation requested...");
    } catch (error) {
      setTrainingStatus("error");
      setTrainingMessage(
        String(error?.message || error),
      );
    }
  };

  const showMapAsset = (asset) => {
    setHistoricalOpen(false);
    setMapAsset(asset);
    setMapVersion(Date.now());
    setMapStatus("success");
    setMapMessage(null);
  };

  const loadMap = async ({ scenario = null } = {}) => {
    setHistoricalOpen(false);
    setMapStatus("loading");
    setMapMessage(null);
    setMapAsset(null);

    try {
      const query = new URLSearchParams({
        country: RN_MODEL_KEY,
        prefer: "html",
      });

      if (scenario) {
        query.set("scenario", scenario);
      }

      const response = await fetch(
        `${API_BASE}/visualizations/rn?${query.toString()}`,
        { cache: "no-store" },
      );

      if (!response.ok) {
        throw new Error(
          await apiErrorToMessage(response),
        );
      }

      const payload = await response.json();
      const assets = Array.isArray(payload.assets)
        ? payload.assets
        : [];

      const asset = assets.find(
        (item) => (
          item?.kind === "map"
          && item?.format === "html"
        ),
      );

      if (!asset) {
        throw new Error(
          "No Radiological-Nuclear prediction map is available.",
        );
      }

      showMapAsset(asset);
    } catch (error) {
      setMapStatus("error");
      setMapMessage(
        String(error?.message || error),
      );
    }
  };

  const updateFormValue = (name, value) => {
    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const runPrediction = async () => {
    if (!selectedModelReady) {
      return;
    }

    predictionAbortRef.current?.abort();

    const controller = new AbortController();
    predictionAbortRef.current = controller;

    setPredictionStatus("loading");
    setPredictionMessage(
      "Running Radiological-Nuclear prediction...",
    );

    try {
      const simulationHours = numberValue(
        form.simulation_hours,
        "Simulation hours",
      );

      if (
        simulationHours < 1
        || simulationHours > 48
      ) {
        throw new Error(
          "Simulation hours must be between 1 and 48.",
        );
      }

      if (!form.start_time) {
        throw new Error("Start time is required.");
      }

      const commonPayload = {
        country: RN_MODEL_KEY,
        threat: THREAT,
        model_type: modelType,
        scenario: form.scenario,
        center_lat: numberValue(
          form.center_lat,
          "Center latitude",
        ),
        center_lon: numberValue(
          form.center_lon,
          "Center longitude",
        ),
        start_time: form.start_time,
        simulation_hours: simulationHours,
      };

      const requestPayload = {
        ...commonPayload,
        wind_u: numberValue(form.wind_u, "Wind U"),
        wind_v: numberValue(form.wind_v, "Wind V"),
        precipitation: numberValue(
          form.precipitation,
          "Precipitation",
        ),
      };

      if (requestPayload.precipitation < 0) {
        throw new Error(
          "Precipitation cannot be negative.",
        );
      }

      const response = await fetch(
        `${API_BASE}/predict_RN_models`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestPayload),
          signal: controller.signal,
        },
      );

      if (!response.ok) {
        throw new Error(
          await apiErrorToMessage(response),
        );
      }

      const result = await response.json();

      setPredictionStatus("success");
      setPredictionMessage(
        result.message
        || "Radiological-Nuclear prediction completed successfully.",
      );

      if (result.visualization_url) {
        showMapAsset({
          title: "Radiological-Nuclear prediction",
          filename: result.output_filename,
          kind: "map",
          format: "html",
          url: result.visualization_url,
        });
      } else {
        await loadMap({
          scenario: form.scenario,
        });
      }
    } catch (error) {
      setPredictionStatus("error");
      setPredictionMessage(
        error?.name === "AbortError"
          ? "Radiological-Nuclear prediction cancelled."
          : String(error?.message || error),
      );
    } finally {
      predictionAbortRef.current = null;
    }
  };

  useEffect(() => {
    checkModels();

    const activeJob = loadStored(ACTIVE_JOB_KEY);

    if (activeJob?.jobId) {
      setTrainingPanelOpen(true);
      setTrainingStatus("loading");
      setTrainingMessage(
        "Reconnecting to Radiological-Nuclear training job...",
      );
      setTrainingProgress(1);
      setTrainingJobId(activeJob.jobId);
      startTrainingPolling(activeJob.jobId);
      return;
    }

    const lastResult = loadStored(LAST_RESULT_KEY);

    if (lastResult?.status === "completed") {
      setTrainingPanelOpen(true);
      setTrainingStatus("success");
      setTrainingMessage(lastResult.message);
      setTrainingProgress(100);
      if (lastResult.modelType) {
        setModelType(lastResult.modelType);
      }
    }

    // Restore stored state only when the page mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      stopTrainingPolling();
      predictionAbortRef.current?.abort();
    };
  }, []);

  const mapUrl = mapAsset?.url
    ? (
      mapAsset.url.startsWith("http")
        ? mapAsset.url
        : `${API_BASE}${mapAsset.url}?v=${mapVersion}`
    )
    : null;

  return (
    <div className="pageContainer">
      <div className="ds-header">
        <h1 className="ds-title">
          Radiological &amp; Nuclear Agents
        </h1>
      </div>

      <div className="rnp-stack">
        <div className="ds-card rnp-filters">
          <div className="rnp-filter-summary">
            <div className="rnp-model-state">
              <span>Default model</span>

              <strong
                className={
                  modelAvailability.default
                    ? "rnp-ready"
                    : "rnp-missing"
                }
              >
                {
                  modelsChecking
                    ? "Checking..."
                    : modelAvailability.default
                      ? "Ready"
                      : "Not trained"
                }
              </strong>
            </div>

            <div className="rnp-model-state">
              <span>Custom model</span>

              <strong
                className={
                  modelAvailability.custom
                    ? "rnp-ready"
                    : "rnp-missing"
                }
              >
                {
                  modelsChecking
                    ? "Checking..."
                    : modelAvailability.custom
                      ? "Ready"
                      : "Not trained"
                }
              </strong>
            </div>
          </div>

          <div className="rnp-actions">
            <button
              className="ds-btn"
              type="button"
              onClick={() => setTrainingPanelOpen(true)}
            >
              Create New AI Models
            </button>

            <button
              className={
                `ds-btn ${
                  !modelsReady
                    ? "ds-btn--disabled"
                    : ""
                }`
              }
              type="button"
              disabled={!modelsReady}
              onClick={() => {
                setPredictionOpen((current) => !current);
                setHistoricalOpen(false);
              }}
            >
              Adopt AI Models - Predict
            </button>

            <button
              className="ds-btn"
              type="button"
              onClick={() => loadMap()}
            >
              Display Latest Prediction
            </button>

            <button
              className="ds-btn rnp-historical-trigger"
              type="button"
              onClick={() => {
                setHistoricalOpen((current) => !current);
                setPredictionOpen(false);
                setMapAsset(null);
                setMapStatus(null);
                setMapMessage(null);
              }}
            >
              Historical Data
            </button>
          </div>
        </div>

        {
          trainingPanelOpen
          && (
            <div className="ds-card rnp-panel">
              <div className="rnp-panel-header">
                <div>
                  <h2>
                    Radiological-Nuclear AI model training
                  </h2>
                  <p>
                    Choose the data source for retraining the model.
                  </p>
                </div>

                {
                  trainingStatus !== "loading"
                  && (
                    <button
                      className="ds-btn"
                      type="button"
                      onClick={() => setTrainingPanelOpen(false)}
                    >
                      Close
                    </button>
                  )
                }
              </div>

              {
                trainingStatus !== "loading"
                && (
                  <>
                    <fieldset className="rnp-model-choice">
                      <legend>Training mode</legend>

                      <label
                        className={
                          `rnp-model-option ${
                            trainingMode === "existing"
                              ? "rnp-model-option--selected"
                              : ""
                          }`
                        }
                      >
                        <input
                          type="radio"
                          name="rn-training-mode"
                          value="existing"
                          checked={trainingMode === "existing"}
                          onChange={() => {
                            setTrainingMode("existing");
                            setTrainingStatus(null);
                            setTrainingMessage(null);
                          }}
                        />
                        <span>
                          <strong>
                            Retrain the existing model
                          </strong>
                          <small>
                            Use the established RN training data,
                            up to 1,000,000 records.
                          </small>
                        </span>
                      </label>

                      <label
                        className={
                          `rnp-model-option ${
                            trainingMode === "custom"
                              ? "rnp-model-option--selected"
                              : ""
                          }`
                        }
                      >
                        <input
                          type="radio"
                          name="rn-training-mode"
                          value="custom"
                          checked={trainingMode === "custom"}
                          onChange={() => {
                            setTrainingMode("custom");
                            setTrainingStatus(null);
                            setTrainingMessage(null);
                          }}
                        />
                        <span>
                          <strong>
                            Retrain the model using custom data
                          </strong>
                          <small>
                            Upload a Radiological-Nuclear CDM CSV
                            from this computer.
                          </small>
                        </span>
                      </label>
                    </fieldset>

                    {
                      trainingMode === "custom"
                      && (
                        <div className="rnp-upload-panel">
                          <label className="rnp-upload-field">
                            <span>Custom RN dataset</span>
                            <input
                              type="file"
                              accept=".csv,text/csv"
                              onChange={(event) => {
                                setCustomDatasetFile(
                                  event.target.files?.[0] || null,
                                );
                                setTrainingStatus(null);
                                setTrainingMessage(null);
                              }}
                            />
                          </label>
                          <small className="rnp-upload-help">
                            CDM columns:{" "}
                            {RN_CDM_COLUMNS.join(", ")}.
                            {" "}Country may be empty.
                          </small>
                        </div>
                      )
                    }

                    <div className="rnp-form-actions">
                      <button
                        className="ds-btn"
                        type="button"
                        onClick={startTraining}
                        disabled={
                          trainingMode === "custom"
                          && !customDatasetFile
                        }
                      >
                        Start training
                      </button>
                    </div>
                  </>
                )
              }

              {
                trainingStatus === "loading"
                && (
                  <div className="rnp-progress-card">
                    <div className="rnp-progress-header">
                      <span className="rnp-progress-title">
                        <span
                          className="rnp-spinner"
                          aria-hidden="true"
                        />
                        Training Radiological-Nuclear AI model
                      </span>
                      <button
                        className="ds-btn"
                        type="button"
                        onClick={cancelTraining}
                      >
                        Cancel training
                      </button>
                    </div>
                    <div className="rnp-progress-message">
                      {trainingMessage}
                    </div>
                    <div className="rnp-progress-meta">
                      <span>Progress</span>
                      <strong>{trainingProgress}%</strong>
                    </div>
                    <div className="rnp-progress-track">
                      <div
                        className="rnp-progress-bar"
                        style={{ width: `${trainingProgress}%` }}
                      />
                    </div>
                  </div>
                )
              }

              {
                trainingStatus === "success"
                && (
                  <div className="rnp-message rnp-message--success">
                    {trainingMessage}
                  </div>
                )
              }
              {
                trainingStatus === "error"
                && (
                  <div className="rnp-message rnp-message--error">
                    {trainingMessage}
                  </div>
                )
              }
            </div>
          )
        }

        {
          predictionOpen
          && (
            <div className="ds-card rnp-panel">
              <div className="rnp-panel-header">
                <div>
                  <h2>
                    Radiological-Nuclear prediction parameters
                  </h2>

                  <p>
                    Configure the scenario and provide the
                    meteorological conditions manually.
                  </p>
                </div>

                <button
                  className="ds-btn"
                  type="button"
                  onClick={() => setPredictionOpen(false)}
                >
                  Close
                </button>
              </div>

              <fieldset className="rnp-model-choice">
                <legend>Prediction model</legend>

                {[
                  ["default", "Default model"],
                  ["custom", "Custom model"],
                ].map(([value, label]) => (
                  <label
                    className={
                      `rnp-model-option ${
                        modelType === value
                          ? "rnp-model-option--selected"
                          : ""
                      }`
                    }
                    key={value}
                  >
                    <input
                      type="radio"
                      name="rn-prediction-model"
                      value={value}
                      checked={modelType === value}
                      disabled={!modelAvailability[value]}
                      onChange={() => setModelType(value)}
                    />
                    <span>
                      <strong>{label}</strong>
                      <small>
                        {modelAvailability[value]
                          ? "Ready for prediction"
                          : "Train this model first"}
                      </small>
                    </span>
                  </label>
                ))}
              </fieldset>

              <div className="rnp-form-grid">
                <label className="rnp-field">
                  <span>Scenario</span>
                  <select
                    value={form.scenario}
                    onChange={(event) => {
                      updateFormValue(
                        "scenario",
                        event.target.value,
                      );
                    }}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </label>

                <label className="rnp-field">
                  <span>Center latitude (degrees)</span>
                  <input
                    type="number"
                    step="any"
                    value={form.center_lat}
                    onChange={(event) => {
                      updateFormValue(
                        "center_lat",
                        event.target.value,
                      );
                    }}
                  />
                </label>

                <label className="rnp-field">
                  <span>Center longitude (degrees)</span>
                  <input
                    type="number"
                    step="any"
                    value={form.center_lon}
                    onChange={(event) => {
                      updateFormValue(
                        "center_lon",
                        event.target.value,
                      );
                    }}
                  />
                </label>

                <label className="rnp-field">
                  <span>Start time</span>
                  <input
                    type="datetime-local"
                    value={form.start_time}
                    onChange={(event) => {
                      updateFormValue(
                        "start_time",
                        event.target.value,
                      );
                    }}
                  />
                </label>

                <label className="rnp-field">
                  <span>Simulation duration (hours)</span>
                  <input
                    type="number"
                    min="1"
                    max="48"
                    step="1"
                    value={form.simulation_hours}
                    onChange={(event) => {
                      updateFormValue(
                        "simulation_hours",
                        event.target.value,
                      );
                    }}
                  />
                </label>

                <label className="rnp-field">
                  <span>Wind U (m/s)</span>
                  <input
                    type="number"
                    step="any"
                    value={form.wind_u}
                    onChange={(event) => {
                      updateFormValue(
                        "wind_u",
                        event.target.value,
                      );
                    }}
                  />
                  <small className="rnp-field-help">
                    East-west component: positive is eastward;
                    negative is westward. Wind from the west at
                    7.2 m/s means U = +7.2.
                  </small>
                </label>

                <label className="rnp-field">
                  <span>Wind V (m/s)</span>
                  <input
                    type="number"
                    step="any"
                    value={form.wind_v}
                    onChange={(event) => {
                      updateFormValue(
                        "wind_v",
                        event.target.value,
                      );
                    }}
                  />
                  <small className="rnp-field-help">
                    North-south component: positive is northward;
                    negative is southward.
                  </small>
                </label>

                <label className="rnp-field">
                  <span>Precipitation (mm)</span>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={form.precipitation}
                    onChange={(event) => {
                      updateFormValue(
                        "precipitation",
                        event.target.value,
                      );
                    }}
                  />
                </label>
              </div>

              <div className="rnp-form-actions">
                <button
                  className="ds-btn"
                  type="button"
                  onClick={runPrediction}
                  disabled={
                    predictionStatus === "loading"
                    || !selectedModelReady
                  }
                >
                  Start prediction
                </button>

                {
                  predictionStatus === "loading"
                  && (
                    <button
                      className="ds-btn"
                      type="button"
                      onClick={() => {
                        predictionAbortRef.current?.abort();
                      }}
                    >
                      Cancel prediction
                    </button>
                  )
                }
              </div>

              {
                predictionStatus
                && (
                  <div
                    className={
                      `rnp-message rnp-message--${predictionStatus}`
                    }
                    aria-live="polite"
                  >
                    {
                      predictionStatus === "loading"
                      && (
                        <span
                          className="rnp-spinner"
                          aria-hidden="true"
                        />
                      )
                    }
                    {predictionMessage}
                  </div>
                )
              }
            </div>
          )
        }

        {
          historicalOpen
          && (
            <div className="ds-card rnp-historical-card">
              <div className="rnp-map-title">
                Historical Data - Radiation-Nuclear Analytics
              </div>

              <iframe
                src={RN_HISTORICAL_DASHBOARD_URL}
                title="Historical Radiation-Nuclear Analytics"
                className="rnp-historical-frame"
                loading="lazy"
              />
            </div>
          )
        }

        {
          mapStatus === "loading"
          && (
            <div className="ds-card rnp-placeholder">
              Loading Radiological-Nuclear map...
            </div>
          )
        }

        {
          mapStatus === "error"
          && (
            <div className="ds-card rnp-message rnp-message--error">
              {mapMessage}
            </div>
          )
        }

        {
          mapStatus === "success"
          && mapUrl
          && !historicalOpen
          && (
            <div className="ds-card rnp-map-card">
              <div className="rnp-map-title">
                Radiological-Nuclear dispersion prediction
              </div>

              <iframe
                key={mapVersion}
                title={
                  mapAsset.title
                  || "Radiological-Nuclear dispersion prediction"
                }
                src={mapUrl}
                className="rnp-map-frame"
              />
            </div>
          )
        }
      </div>
    </div>
  );
}
